/**
 * zod schemas for every `/api/v1` payload in ARCHITECTURE.md §7.
 * Shared by the Hono server (request validation) and the React app (forms).
 *
 * Red lines enforced at the schema boundary:
 *   14.1 singing → `tone` / `realized_tone` must be null and 字头/字腹/字尾 required;
 *        reading → `onset` / `nucleus` / `coda` must be absent or null.
 *   14.4 tri-state: every score is `number|null`, never defaulted to 0.
 *   14.5 material chars must carry a non-empty `pinyin_locked`.
 */
import { z } from "zod";
import { MAX_DURATION_CAP_MS } from "./wav.js";
import {
  ANNOTATOR_TRACKS,
  DEFAULT_COLLECTION,
  ASSIGNMENT_STATES,
  BATCH_STATES,
  DIALECT_GROUPS,
  INVITABLE_ROLES,
  DOMAINS,
  ELICITATIONS,
  GENDERS,
  MARK_TYPES,
  MATERIAL_KINDS,
  MATERIAL_RIGHTS,
  RECORDING_PASSES,
  RECORDING_STATES,
  ROLES,
  TASKS,
  TRAINING_LEVELS,
  TRIAL_TYPES,
  ZHE_NAMES,
} from "./types.js";
import type { Task } from "./types.js";

/** [0,1] score or tri-state null. */
export const scoreSchema = z.number().min(0).max(1).nullable();
export const toneClassSchema = z.number().int().min(0).max(4).nullable();

export const roleSchema = z.enum(ROLES);
export const taskSchema = z.enum(TASKS);
export const domainSchema = z.enum(DOMAINS);
export const elicitationSchema = z.enum(ELICITATIONS);
export const materialKindSchema = z.enum(MATERIAL_KINDS);
export const markTypeSchema = z.enum(MARK_TYPES);
export const zheSchema = z.enum(ZHE_NAMES);
export const batchStateSchema = z.enum(BATCH_STATES);
export const assignmentStateSchema = z.enum(ASSIGNMENT_STATES);
export const recordingStateSchema = z.enum(RECORDING_STATES);
export const trialTypeSchema = z.enum(TRIAL_TYPES);
export const dialectGroupSchema = z.enum(DIALECT_GROUPS);
export const trainingLevelSchema = z.enum(TRAINING_LEVELS);
export const genderSchema = z.enum(GENDERS);
export const recordingPassSchema = z.enum(RECORDING_PASSES);

// --------------------------------------------------------------------------- //
// auth
// --------------------------------------------------------------------------- //

export const loginSchema = z.object({
  username: z.string().min(1).max(64),
  password: z.string().min(8).max(200),
  /** ACCOUNTS.md §3 「记住此设备」 — 30 d instead of 24 h. */
  remember: z.boolean().default(false),
});
export type LoginPayload = z.infer<typeof loginSchema>;

/** POST auth/consent — 红线 14.6 三授权. */
export const consentSchema = z.object({
  train: z.boolean(),
  display: z.boolean(),
  commercial: z.boolean(),
  minor_guardian_signed: z.boolean().optional(),
});
export type ConsentPayload = z.infer<typeof consentSchema>;

/** A1 学生自助注册 — `POST auth/register` (public, rate-limited). */
export const usernameSchema = z
  .string()
  .min(3)
  .max(32)
  .regex(/^[a-z0-9_]+$/, "username may only contain a-z, 0-9 and _");

/** B7 — self-set passwords are ≥10 characters (login still accepts the ≥8 legacy ones). */
export const strongPasswordSchema = z.string().min(10).max(200);

export const registerSchema = z.object({
  invite_code: z.string().min(1).max(64),
  username: usernameSchema,
  password: strongPasswordSchema,
  display_name: z.string().min(1).max(64),
  /**
   * Optional at signup. Empty / omitted means the student finishes them later
   * (完善资料). A present value is still validated.
   */
  dialect_l1: z.array(dialectGroupSchema).max(8).optional(),
  hometown: z.string().max(64).nullable().optional(),
  training_level: trainingLevelSchema.optional(),
  birth_year: z
    .number()
    .int()
    .min(1900)
    .max(new Date().getFullYear())
    .nullable()
    .optional(),
  gender: genderSchema.nullable().optional(),
  contact: z.string().max(120).nullable().optional(),
  /** 红线 14.6 — same page as the account fields, not a later wall. */
  consent: consentSchema.optional(),
});
export type RegisterPayload = z.infer<typeof registerSchema>;

/**
 * A1 `PATCH me/profile` — a student edits their own background.
 * `class_id` is never editable here, and `birth_year` is rejected by the server
 * once the course team has verified the speaker.
 */
export const profileUpdateSchema = z
  .object({
    display_name: z.string().min(1).max(64).optional(),
    dialect_l1: z.array(dialectGroupSchema).min(1).max(8).optional(),
    hometown: z.string().max(64).nullable().optional(),
    training_level: trainingLevelSchema.optional(),
    contact: z.string().max(120).nullable().optional(),
    gender: genderSchema.nullable().optional(),
    birth_year: z.number().int().min(1900).max(new Date().getFullYear()).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "empty profile patch" });
export type ProfileUpdatePayload = z.infer<typeof profileUpdateSchema>;

// --------------------------------------------------------------------------- //
// classes & invite codes (A1)
// --------------------------------------------------------------------------- //

/**
 * Human-typeable code alphabet (Crockford base32 without `0 O 1 I L U`), used for
 * both class invite codes (`XH-VOCAL2`) and staff invitations (`XH-4K7M-2QRD`).
 */
export const INVITE_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTVWXYZ";

export const inviteCodeSchema = z
  .string()
  .min(4)
  .max(32)
  .regex(/^[A-Za-z0-9-]+$/, "invite code may only contain letters, digits and -");

export const classCreateSchema = z.object({
  id: z.string().min(1).max(64),
  name: z.string().min(1).max(64),
  invite_code: inviteCodeSchema.optional(),
  invite_enabled: z.boolean().default(true),
});
export type ClassCreatePayload = z.infer<typeof classCreateSchema>;

/** `POST classes/:id/invite` — rotate the code and/or flip the switch. */
export const classInviteSchema = z
  .object({
    rotate: z.boolean().default(false),
    invite_code: inviteCodeSchema.optional(),
    enabled: z.boolean().optional(),
  })
  .refine((v) => v.rotate || v.invite_code !== undefined || v.enabled !== undefined, {
    message: "nothing to do: pass rotate, invite_code or enabled",
  });
export type ClassInvitePayload = z.infer<typeof classInviteSchema>;

// --------------------------------------------------------------------------- //
// ACCOUNTS.md — bootstrap, invitations, password reset, sessions
// --------------------------------------------------------------------------- //

export const annotatorTrackSchema = z.enum(ANNOTATOR_TRACKS);
export const invitableRoleSchema = z.enum(INVITABLE_ROLES);

/** §1 `POST auth/setup` — only while `app_user` is empty. */
export const setupSchema = z.object({
  token: z.string().min(8).max(200),
  username: usernameSchema,
  password: strongPasswordSchema,
  display_name: z.string().min(1).max(64),
  /** The owner normally runs the course as well. */
  roles: z.array(invitableRoleSchema).min(1).default(["admin", "course_team"]),
});
export type SetupPayload = z.infer<typeof setupSchema>;

/** §2 `POST admin/invitations` and `POST classes/:id/invitations`. */
export const invitationCreateSchema = z.object({
  roles: z.array(invitableRoleSchema).min(1),
  class_id: z.string().min(1).nullable().optional(),
  annotator_track: annotatorTrackSchema.nullable().optional(),
  note: z.string().max(200).nullable().optional(),
  label: z.string().max(80).nullable().optional(),
  /** `null` = unlimited. Omitted on the admin route stays single-use. */
  max_uses: z.number().int().min(1).max(100_000).nullable().optional(),
  expires_in_hours: z
    .number()
    .int()
    .min(1)
    .max(24 * 365)
    .optional(),
});
export type InvitationCreatePayload = z.infer<typeof invitationCreateSchema>;

/**
 * 标注成果授权书 — accepted at invite time by annotator / adjudicator /
 * teacher / course_team. Admin-only invitations may omit it.
 */
export const workAgreementSchema = z.object({
  version: z.string().min(1).max(32),
  accepted: z.literal(true),
});
export type WorkAgreementPayload = z.infer<typeof workAgreementSchema>;

/** §2 `POST auth/invite/:code` — the invitee picks their own credentials. */
export const invitationAcceptSchema = z.object({
  username: usernameSchema,
  password: strongPasswordSchema,
  display_name: z.string().min(1).max(64),
  /** Required for every role in `WORK_AGREEMENT_ROLES`; the server enforces it. */
  work_agreement: workAgreementSchema.optional(),
  /** Accepted here too so `POST auth/invite?code=…` works without a path param. */
  code: z.string().min(4).max(64).optional(),
});

/** Logged-in redeem: add the invitation's roles to the current account. */
export const invitationJoinSchema = z.object({
  work_agreement: workAgreementSchema.optional(),
});
export type InvitationJoinPayload = z.infer<typeof invitationJoinSchema>;

/** `PATCH account/profile` — display name and username, any role. */
export const accountProfileSchema = z
  .object({
    display_name: z.string().min(1).max(64).optional(),
    username: usernameSchema.optional(),
  })
  .refine((v) => v.display_name !== undefined || v.username !== undefined, {
    message: "empty profile patch",
  });
export type AccountProfilePayload = z.infer<typeof accountProfileSchema>;

export const phoneStartSchema = z.object({
  phone: z.string().trim().min(8).max(20),
});
export type PhoneStartPayload = z.infer<typeof phoneStartSchema>;

/** `POST auth/recover/sms` — code from the SMS plus a new password. */
export const smsRecoverSchema = z.object({
  account: z.string().trim().min(1).max(160),
  code: z.string().trim().regex(/^\d{6}$/, "the code is six digits"),
  password: strongPasswordSchema,
});
export type SmsRecoverPayload = z.infer<typeof smsRecoverSchema>;

/** Replace one user's roles inside a single class. */
export const classMemberRolesSchema = z.object({
  roles: z.array(roleSchema).min(1),
});
export type ClassMemberRolesPayload = z.infer<typeof classMemberRolesSchema>;
export type InvitationAcceptPayload = z.infer<typeof invitationAcceptSchema>;

/** §4 `POST auth/reset/:token`. */
export const passwordResetSchema = z.object({
  password: strongPasswordSchema,
});
export type PasswordResetPayload = z.infer<typeof passwordResetSchema>;

/** §3 `POST account/password`. */
export const passwordChangeSchema = z.object({
  current: z.string().min(1).max(200),
  next: strongPasswordSchema,
});
export type PasswordChangePayload = z.infer<typeof passwordChangeSchema>;

// --------------------------------------------------------------------------- //
// §4 忘记密码自助 (SMTP) — 可选邮箱 + 通用应答
// --------------------------------------------------------------------------- //

/** A verified contact address. Lower-cased and trimmed by the server. */
export const emailAddressSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(5)
  .max(160)
  .regex(/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/, "email address looks malformed");

/** `POST account/email` — start verification of an optional contact address. */
export const emailStartSchema = z.object({ email: emailAddressSchema });
export type EmailStartPayload = z.infer<typeof emailStartSchema>;

/** `POST account/email/verify` — the 6-digit code from that email. */
export const emailVerifySchema = z.object({
  code: z.string().trim().regex(/^\d{6}$/, "the code is six digits"),
});
export type EmailVerifyPayload = z.infer<typeof emailVerifySchema>;

/**
 * `POST auth/forgot` — 用户名或已验证邮箱.
 *
 * The response is **always** the same generic body, whatever the account state
 * is: no account enumeration, ever (ACCOUNTS.md §4).
 */
export const forgotPasswordSchema = z.object({
  account: z.string().trim().min(1).max(160),
});
export type ForgotPasswordPayload = z.infer<typeof forgotPasswordSchema>;

// --------------------------------------------------------------------------- //
// materials
// --------------------------------------------------------------------------- //

export const materialCharSchema = z.object({
  char: z.string().min(1).max(4),
  // 红线 14.5 — locked pinyin, no G2P fallback
  pinyin_locked: z.string().min(1),
  initial: z
    .string()
    .regex(/^ZH_I_[A-Z]+$/)
    .nullable(),
  final: z
    .string()
    .regex(/^ZH_F_[A-Z]+$/)
    .nullable(),
  tone: toneClassSchema,
  zhe: zheSchema.nullable(),
  confusion_risk: z.array(z.string()).default([]),
});
export type MaterialCharPayload = z.infer<typeof materialCharSchema>;

export const materialSchema = z.object({
  id: z.string().min(1),
  kind: materialKindSchema,
  task: taskSchema,
  domain: domainSchema,
  text: z.string().min(1),
  chars: z.array(materialCharSchema).min(1),
  confusion_classes: z.array(z.string()).default([]),
  version: z.string().default("v1.0"),
  functional_load: z.enum(["high", "mid", "low"]).nullable().optional(),
  meaning_collision: z.string().nullable().optional(),
  prescription_ids: z.array(z.string()).optional(),
  /** Authored collection (`corpus_v1` default, `ai_test_v1` for 《AI 正音测试》). */
  collection: z.string().min(1).max(64).default(DEFAULT_COLLECTION),
  /** 《AI 正音测试》一级/二级/三级; null for everything else. */
  level: z.number().int().min(1).max(3).nullable().default(null),
  /** Section inside the collection, e.g. `3.3 辅音·双唇音 b p`. */
  section: z.string().max(120).nullable().default(null),
  /** Order inside (collection, level, section). */
  sort_key: z.number().int().min(0).default(0),
  /** Per-material recording cap; `null` = the §9 22 s default. */
  max_duration_ms: z.number().int().positive().max(MAX_DURATION_CAP_MS).nullable().default(null),
  /** Per-material take count override; `null` = the task rule. */
  passes: z.number().int().min(1).max(10).nullable().default(null),
  /** 著作权状态；`null` = 未评估（P0 语料库全是原创或传统作品）. */
  rights: z.enum(MATERIAL_RIGHTS).nullable().default(null),
  rights_note: z.string().max(500).nullable().default(null),
});
export type MaterialPayload = z.infer<typeof materialSchema>;

/** POST materials/import */
export const materialImportSchema = z.object({
  materials: z.array(materialSchema).min(1),
  /** replace an existing (unfrozen) material of the same id */
  overwrite: z.boolean().default(false),
});
export type MaterialImportPayload = z.infer<typeof materialImportSchema>;

/** POST materials/:id/freeze */
export const materialFreezeSchema = z.object({
  version: z.string().min(1),
});
export type MaterialFreezePayload = z.infer<typeof materialFreezeSchema>;

// --------------------------------------------------------------------------- //
// speakers
// --------------------------------------------------------------------------- //

export const speakerImportRowSchema = z.object({
  user_id: z.string().min(1).optional(),
  username: z.string().min(1),
  display_name: z.string().min(1).optional(),
  speaker_code: z.string().min(1),
  dialect_l1: z.array(z.string()).default([]),
  training_level: z.string().default(""),
  class_id: z.string().nullable().default(null),
  is_minor: z.boolean().default(false),
});

export const speakerImportSchema = z.object({
  speakers: z.array(speakerImportRowSchema).min(1),
});
export type SpeakerImportPayload = z.infer<typeof speakerImportSchema>;

/** PATCH speakers/:id */
export const speakerUpdateSchema = z.object({
  dialect_l1: z.array(z.string()).optional(),
  training_level: z.string().optional(),
  class_id: z.string().nullable().optional(),
  minor_guardian_signed: z.boolean().optional(),
  /** A1 课程团队核验自助注册的说话人. */
  verified: z.boolean().optional(),
  hometown: z.string().max(64).nullable().optional(),
  birth_year: z.number().int().min(1900).max(new Date().getFullYear()).nullable().optional(),
  gender: genderSchema.nullable().optional(),
  contact: z.string().max(120).nullable().optional(),
});
export type SpeakerUpdatePayload = z.infer<typeof speakerUpdateSchema>;

// --------------------------------------------------------------------------- //
// batches / dispatch
// --------------------------------------------------------------------------- //

export const batchCreateSchema = z.object({
  id: z.string().min(1),
  phase: z.string().min(1),
  tasks: z.array(taskSchema).min(1),
  material_ids: z.array(z.string()).min(1),
  /**
   * B5 校准批：录音发给**全部**在岗标注专家（不是一对），结果只用来算周度 IAA，
   * 永不合并、永不进导出。
   */
  calibration: z.boolean().optional(),
});
export type BatchCreatePayload = z.infer<typeof batchCreateSchema>;

export const batchPublishSchema = z.object({
  confirm: z.literal(true),
});
export type BatchPublishPayload = z.infer<typeof batchPublishSchema>;

export const batchDispatchSchema = z
  .object({
    speaker_ids: z.array(z.string()).min(1),
    material_ids: z.array(z.string()).optional(),
    /**
     * `manual` — the given material list verbatim;
     * `dialect_prior` / `round_robin` — everything, only re-ordered;
     * `session_plan` — A2 §2.2 22-minute session: all T2 + 6 T3 + 2 T4 + 2 songs + T7.
     */
    strategy: z.enum(["manual", "dialect_prior", "round_robin", "session_plan"]).default("manual"),
    /** T5↔T6 成对派发 */
    pair_t5_t6: z.boolean().default(true),
    /** 复测轮次: 0 = 基线, 1 = 第 4 周, 2 = 第 8 周 … */
    retest_round: z.number().int().min(0).max(9).default(0),
    /**
     * Re-publishing the same template to the same class would hand every student
     * the same items a second time (the UNIQUE constraint is per batch). The
     * server refuses that with 409 `DUPLICATE_BASELINE`; set this only after the
     * operator has seen the warning and chosen to go ahead anyway.
     */
    allow_duplicate: z.boolean().default(false),
  })
  .refine((v) => v.strategy !== "manual" || (v.material_ids?.length ?? 0) > 0, {
    message: "manual dispatch requires material_ids",
    path: ["material_ids"],
  });
export type BatchDispatchPayload = z.infer<typeof batchDispatchSchema>;

// --------------------------------------------------------------------------- //
// B4 派发式标注（标注专家配对）
// --------------------------------------------------------------------------- //

/** 配对来源：`auto` 自动均衡，`manual` 指定一对；校准批由服务端强制为 `calibration`。 */
export const ANNOTATION_ASSIGN_MODES = ["auto", "manual"] as const;
export const annotationAssignModeSchema = z.enum(ANNOTATION_ASSIGN_MODES);

/**
 * `POST batches/:id/assign-annotators` — 把批次里（或指定的）已质检录音配给标注专家。
 *
 * `dry_run: true` 只返回预览（每位专家的负载），不写任何一行；UI 的
 * 「分配标注」按钮先预览、再确认。校准批忽略 `mode` 与 `annotator_ids`，
 * 一律发给全部在岗专家。
 */
export const annotationAssignSchema = z
  .object({
    mode: annotationAssignModeSchema.default("auto"),
    /** 留空 = 该批次全部可分配的录音。 */
    recording_ids: z.array(z.string().min(1)).max(2000).optional(),
    /** `manual` 必填：正好两位专家 (E1, E2)。 */
    annotator_ids: z.array(z.string().min(1)).length(2).optional(),
    dry_run: z.boolean().default(false),
  })
  .refine((v) => v.mode !== "manual" || (v.annotator_ids?.length ?? 0) === 2, {
    message: "manual pairing requires exactly two annotator_ids",
    path: ["annotator_ids"],
  })
  .refine((v) => (v.annotator_ids?.[0] ?? "a") !== (v.annotator_ids?.[1] ?? "b"), {
    message: "a recording cannot be assigned to the same annotator twice",
    path: ["annotator_ids"],
  });
export type AnnotationAssignPayload = z.infer<typeof annotationAssignSchema>;

/**
 * `POST batches/:id/unassign-annotators` — 撤销分配。
 * 已经提交过判断的那个人不会被撤销（双盲记录一旦写下就固定）。
 */
export const annotationUnassignSchema = z
  .object({
    recording_ids: z.array(z.string().min(1)).max(2000).optional(),
    annotator_ids: z.array(z.string().min(1)).max(50).optional(),
  })
  .refine((v) => (v.recording_ids?.length ?? 0) > 0 || (v.annotator_ids?.length ?? 0) > 0, {
    message: "pass recording_ids and/or annotator_ids",
    path: ["recording_ids"],
  });
export type AnnotationUnassignPayload = z.infer<typeof annotationUnassignSchema>;

// --------------------------------------------------------------------------- //
// C1 复测轮次向导
// --------------------------------------------------------------------------- //

/** 复测轮次：1 = 第 4 周，2 = 第 8 周（§2.2 的两次复测）。 */
export const RETEST_ROUNDS = [1, 2] as const;

/**
 * `POST batches/:id/retest` — 从基线批次生成一轮复测：同一批学生、同一批素材、
 * 同样的设置。`dry_run: true` 只返回「将会创建什么」。
 */
export const retestCreateSchema = z.object({
  round: z
    .number()
    .int()
    .refine((v): v is 1 | 2 => v === 1 || v === 2, { message: "复测轮次只能是 1 或 2" }),
  /** 新批次编号，缺省 `<源批次>-R<轮次>`。 */
  batch_id: z.string().min(1).max(64).optional(),
  title: z.string().min(1).max(120).optional(),
  dry_run: z.boolean().default(false),
});
export type RetestCreatePayload = z.infer<typeof retestCreateSchema>;

// --------------------------------------------------------------------------- //
// recordings
// --------------------------------------------------------------------------- //

/** POST recordings — the JSON metadata part of the multipart upload. */
export const recordingUploadSchema = z.object({
  assignment_id: z.string().min(1),
  elicitation: elicitationSchema.default("natural"),
  session_id: z.string().min(1),
  /**
   * Client-measured; the server re-derives it from the WAV header and enforces
   * the *per-material* cap (`material.max_duration_ms ?? 22 000`). This schema
   * only rejects anything past the absolute ceiling — a normal 22 s item that
   * comes in at 23 s is refused by `routes/recordings.ts` with 409 `TOO_LONG`.
   */
  duration_ms: z.number().int().positive().max(MAX_DURATION_CAP_MS),
  sample_rate: z.number().int().positive(),
  paired_with: z.string().nullable().optional(),
  retest_of: z.string().nullable().optional(),
  /**
   * B2 离线上传队列 — idempotency key minted by the client for one take and
   * reused by every retry. The server dedupes on `(assignment_id, upload_key)`,
   * so a补传 that already reached the server can never create a second
   * recording. Absent on clients that do not queue.
   */
  upload_key: z.string().min(8).max(100).nullable().optional(),
});
export type RecordingUploadPayload = z.infer<typeof recordingUploadSchema>;

/** Header carrying `upload_key` when the body cannot (kept in sync with the field). */
export const UPLOAD_KEY_HEADER = "x-upload-key";

/**
 * A7 环境检测 — the `client_meta` blob of an upload. Unknown keys are kept
 * (device / UA / level statistics), the three below are read by the QC screen.
 */
export const recordingClientMetaSchema = z.looseObject({
  /** T3 双遍: 慢速 / 正常. */
  pass: recordingPassSchema.nullable().optional(),
  /** RMS of the 2 s ambient probe recorded before the session. */
  noise_floor_rms: z.number().nullable().optional(),
  /** Estimated SNR in dB (§9 requires ≥ 45). */
  snr_db: z.number().nullable().optional(),
});
export type RecordingClientMetaPayload = z.infer<typeof recordingClientMetaSchema>;

/** POST recordings/:id/qc (admin) */
export const recordingQcSchema = z.object({
  decision: z.enum(["qc_ok", "qc_reject"]),
  clipping: z.boolean().default(false),
  silence_ratio: z.number().min(0).max(1).nullable().default(null),
  too_long: z.boolean().default(false),
  note: z.string().max(500).nullable().default(null),
});
export type RecordingQcPayload = z.infer<typeof recordingQcSchema>;

// --------------------------------------------------------------------------- //
// judgment (POST annotate/:recordingId)
// --------------------------------------------------------------------------- //

export const charJudgmentSchema = z.object({
  char_index: z.number().int().min(0),
  initial: scoreSchema,
  final: scoreSchema,
  tone: scoreSchema.optional().default(null),
  mark_type: markTypeSchema.nullable().default(null),
  realized: z.string().nullable().default(null),
  realized_tone: toneClassSchema.optional().default(null),
  onset: scoreSchema.optional(),
  nucleus: scoreSchema.optional(),
  coda: scoreSchema.optional(),
  zhe_realized: zheSchema.nullable().optional(),
  confusion_class: z.string().nullable().optional(),
  note: z.string().max(500).nullable().optional(),
});

export const readingUtteranceSchema = z.object({
  domain: z.literal("reading"),
  accuracy: scoreSchema,
  completeness: scoreSchema,
  fluency: scoreSchema,
  prosodic: scoreSchema,
  total: scoreSchema,
});

export const singingUtteranceSchema = z.object({
  domain: z.literal("singing"),
  onset: scoreSchema,
  nucleus: scoreSchema,
  coda: scoreSchema,
  intelligibility: scoreSchema,
  rhythm: scoreSchema,
});

export const utteranceJudgmentSchema = z.discriminatedUnion("domain", [
  readingUtteranceSchema,
  singingUtteranceSchema,
]);

const judgmentBase = z.object({
  domain: domainSchema,
  chars: z.array(charJudgmentSchema).min(1),
  utterance: utteranceJudgmentSchema,
  listening_effort: scoreSchema.default(null),
  accentedness: scoreSchema.default(null),
  note: z.string().max(2000).nullable().optional(),
  elapsed_ms: z.number().int().nonnegative().nullable().optional(),
});

/**
 * POST annotate/:recordingId — one expert's independent judgment.
 * 红线 14.1 is enforced here, not downstream.
 */
export const judgmentSubmitSchema = judgmentBase.superRefine((v, ctx) => {
  if (v.utterance.domain !== v.domain) {
    ctx.addIssue({
      code: "custom",
      path: ["utterance", "domain"],
      message: `utterance rubric domain "${v.utterance.domain}" != judgment domain "${v.domain}"`,
    });
  }
  v.chars.forEach((c, i) => {
    if (v.domain === "singing") {
      // 红线 14.1: 歌唱行不套普通话声调
      if (c.tone !== null && c.tone !== undefined) {
        ctx.addIssue({
          code: "custom",
          path: ["chars", i, "tone"],
          message: "红线14.1: tone must be null in the singing domain",
        });
      }
      if (c.realized_tone !== null && c.realized_tone !== undefined) {
        ctx.addIssue({
          code: "custom",
          path: ["chars", i, "realized_tone"],
          message: "红线14.1: realized_tone must be null in the singing domain",
        });
      }
      for (const dim of ["onset", "nucleus", "coda"] as const) {
        if (c[dim] === undefined) {
          ctx.addIssue({
            code: "custom",
            path: ["chars", i, dim],
            message: `singing judgment requires ${dim} (字头/字腹/字尾), null when unlabeled`,
          });
        }
      }
    } else {
      for (const dim of ["onset", "nucleus", "coda"] as const) {
        if (c[dim] !== undefined && c[dim] !== null) {
          ctx.addIssue({
            code: "custom",
            path: ["chars", i, dim],
            message: `${dim} is a singing-only dimension and must be absent in reading`,
          });
        }
      }
      if (c.zhe_realized !== undefined && c.zhe_realized !== null) {
        ctx.addIssue({
          code: "custom",
          path: ["chars", i, "zhe_realized"],
          message: "zhe_realized is a singing-only dimension",
        });
      }
    }
  });
});
export type JudgmentSubmitPayload = z.infer<typeof judgmentSubmitSchema>;

// --------------------------------------------------------------------------- //
// arbitration
// --------------------------------------------------------------------------- //

/** POST arbitration/:recordingId/:charIndex/:dim */
export const arbitrationDecisionSchema = z
  .object({
    /** null = utterance-level dimension */
    char_index: z.number().int().min(0).nullable(),
    dimension: z.string().min(1),
    decision: z.enum(["value", "split"]),
    value: z.union([z.number(), z.string()]).nullable().optional(),
    note: z.string().max(1000).nullable().optional(),
  })
  .refine((v) => v.decision !== "value" || v.value !== null, {
    message: 'decision "value" requires a value (use "split" to mask — 红线14.4)',
    path: ["value"],
  })
  .refine(
    (v) => v.decision !== "value" || typeof v.value !== "number" || (v.value >= 0 && v.value <= 1),
    { message: "adjudicated score must be in [0,1]", path: ["value"] },
  );
export type ArbitrationDecisionPayload = z.infer<typeof arbitrationDecisionSchema>;

// --------------------------------------------------------------------------- //
// 复评 / re-review tier (A4 · §5.4 「讨论」档 0.10 < |Δ| ≤ 0.25)
// --------------------------------------------------------------------------- //

/** `POST annotate/rereview/:id` — confirm (same value) or revise. */
export const rereviewSubmitSchema = z.object({
  value: scoreSchema,
  note: z.string().max(1000).nullable().optional(),
});
export type RereviewSubmitPayload = z.infer<typeof rereviewSubmitSchema>;

// --------------------------------------------------------------------------- //
// golden items (A5 · §1.5)
// --------------------------------------------------------------------------- //

/** `POST recordings/:id/golden` — `answer: null` clears the golden flag. */
export const goldenSetSchema = z.object({
  answer: z.array(charJudgmentSchema).min(1).nullable(),
  note: z.string().max(500).nullable().optional(),
});
export type GoldenSetPayload = z.infer<typeof goldenSetSchema>;

// --------------------------------------------------------------------------- //
// perception (T7)
// --------------------------------------------------------------------------- //

export const perceptionTrialSchema = z.object({
  trial_type: trialTypeSchema,
  stimuli: z.array(z.string().min(1)).min(1).max(3),
  response: z.string().nullable(),
  correct: z.boolean().nullable(),
  rt_ms: z.number().int().nonnegative().nullable(),
  confusion_class: z.string().nullable().optional(),
  session_id: z.string().nullable().optional(),
});
export type PerceptionTrialPayload = z.infer<typeof perceptionTrialSchema>;

export const perceptionTrialsSchema = z.object({
  trials: z.array(perceptionTrialSchema).min(1).max(500),
});
export type PerceptionTrialsPayload = z.infer<typeof perceptionTrialsSchema>;

// --------------------------------------------------------------------------- //
// export
// --------------------------------------------------------------------------- //

/** POST export/preview — runs the audit without freezing. */
export const exportPreviewSchema = z
  .object({
    recording_ids: z.array(z.string()).optional(),
    batch_ids: z.array(z.string()).optional(),
    include_induced: z.boolean().default(false),
  })
  .refine((v) => (v.recording_ids?.length ?? 0) > 0 || (v.batch_ids?.length ?? 0) > 0, {
    message: "export requires recording_ids or batch_ids",
    path: ["recording_ids"],
  });
export type ExportPreviewPayload = z.infer<typeof exportPreviewSchema>;

/** POST export/freeze — immutable version (§9 版本治理). */
export const exportFreezeSchema = z.object({
  version: z.string().regex(/^\d+\.\d+\.\d+$/, "version must be semver, e.g. 1.0.0"),
  recording_ids: z.array(z.string()).min(1),
  note: z.string().max(1000).nullable().optional(),
});
export type ExportFreezePayload = z.infer<typeof exportFreezeSchema>;

// --------------------------------------------------------------------------- //
// queue filters
// --------------------------------------------------------------------------- //

/**
 * C3 教师端班级覆盖热图 — `GET teacher/coverage`.
 *
 * `by` picks the column axis: the six 采集环节 (T2…T7) or the 14 混淆类.
 * `class_id` narrows a course_team / admin request; a plain `teacher` is always
 * scoped to the classes they actually teach, whatever they ask for.
 */
export const COVERAGE_AXES = ["stage", "confusion"] as const;
export type CoverageAxis = (typeof COVERAGE_AXES)[number];

/** The task stages the heatmap shows, in screen order. */
export const COVERAGE_STAGES = [
  "T2_diagnostic",
  "T3_tongue_twister",
  "T4_passage",
  "T5_lyric_reading",
  "T6_singing",
  "T7_perception",
] as const satisfies readonly Task[];

export const coverageQuerySchema = z.object({
  class_id: z.string().min(1).max(64).optional(),
  by: z.enum(COVERAGE_AXES).default("stage"),
});
export type CoverageQuery = z.infer<typeof coverageQuerySchema>;

export const annotateQueueQuerySchema = z.object({
  batch_id: z.string().optional(),
  task: taskSchema.optional(),
  domain: domainSchema.optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type AnnotateQueueQuery = z.infer<typeof annotateQueueQuerySchema>;
